import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Typography } from "@mui/material";
import { useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Region } from "../types";
import { type HandlePosition, useRegionDrag } from "./useRegionDrag";

const HANDLE_SIZE = 10;
const VERY_SMALL_WIDTH_THRESHOLD = 10;
const VERY_SMALL_PX_THRESHOLD = 100;

const HANDLES: { position: HandlePosition; cursor: string; style: object }[] = [
  {
    position: "nw",
    cursor: "nw-resize",
    style: { top: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 },
  },
  {
    position: "ne",
    cursor: "ne-resize",
    style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 },
  },
  {
    position: "sw",
    cursor: "sw-resize",
    style: { bottom: -HANDLE_SIZE / 2, left: -HANDLE_SIZE / 2 },
  },
  {
    position: "se",
    cursor: "se-resize",
    style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 },
  },
];

interface DraggableRegionProps {
  region: Region;
  otherRegions: Region[];
  containerRef: React.RefObject<HTMLDivElement | null>;
  onUpdate: (id: string, partial: Partial<Region>) => void;
  onRemove: (id: string) => void;
  canRemove: boolean;
  isPanMode?: boolean;
  isPanning?: boolean;
}

export const DraggableRegion = ({
  region,
  otherRegions,
  containerRef,
  onUpdate,
  onRemove,
  canRemove,
  isPanMode = false,
  isPanning = false,
}: DraggableRegionProps) => {
  const { t } = useTranslation();
  const regionRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const [regionPxWidth, setRegionPxWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = regionRef.current;
    if (!el) return;
    setRegionPxWidth(el.offsetWidth);
    const observer = new ResizeObserver(([entry]) => {
      setRegionPxWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const isVerySmall =
    region.width < VERY_SMALL_WIDTH_THRESHOLD ||
    (regionPxWidth !== null && regionPxWidth < VERY_SMALL_PX_THRESHOLD);
  const showLabelBar = !isVerySmall || isHovered;
  const { startDrag } = useRegionDrag({
    region,
    otherRegions,
    containerRef,
    onUpdate,
  });

  return (
    <Box
      ref={regionRef}
      data-region
      sx={{
        position: "absolute",
        left: `${region.x}%`,
        top: `${region.y}%`,
        width: `${region.width}%`,
        height: `${region.height}%`,
        border: `2px solid ${region.color}`,
        cursor: isPanMode ? (isPanning ? "grabbing" : "grab") : "move",
        touchAction: "none",
        userSelect: "none",
      }}
      onPointerDown={(e) => startDrag(e, "move")}
      onPointerEnter={() => setIsHovered(true)}
      onPointerLeave={() => setIsHovered(false)}
    >
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          backgroundColor: region.color,
          opacity: 0.07,
          pointerEvents: "none",
        }}
      />

      {showLabelBar && (
        <Box
          sx={{
            position: "absolute",
            top: isVerySmall ? "auto" : 0,
            bottom: isVerySmall ? "100%" : "auto",
            left: isVerySmall ? "50%" : 0,
            transform: isVerySmall ? "translateX(-50%)" : "none",
            paddingBottom: isVerySmall ? "var(--spacing6, 6px)" : 0,
            ...(isVerySmall ? {} : { maxWidth: "100%" }),
            pointerEvents: isVerySmall ? "auto" : "none",
            zIndex: isVerySmall ? 1 : "auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: region.color,
              color: "var(--global-neutral-white, #fff)",
              padding: "var(--spacing2, 2px) var(--spacing6, 6px)",
              fontSize: "var(--body-b3-12-regular-size, 12px)",
              lineHeight: "var(--body-b3-12-regular-line-height, 18px)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              minWidth: 0,
              ...(isVerySmall && {
                borderRadius: "var(--spacing4, 4px)",
              }),
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "inherit",
                color: "var(--global-neutral-white, #fff)",
              }}
            >
              {isVerySmall
                ? region.label
                : t("ldp_explore_image_region_label", { label: region.label })}
            </Typography>

            {canRemove && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(region.id);
                }}
                sx={{
                  width: 18,
                  height: 18,
                  flexShrink: 0,
                  marginLeft: "var(--spacing4, 4px)",
                  padding: 0,
                  color: "var(--global-neutral-white, #fff)",
                  pointerEvents: "auto",
                  "&:hover": { opacity: 0.7 },
                }}
              >
                <CloseIcon sx={{ fontSize: 14 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      {HANDLES.map(({ position, cursor, style }) => (
        <Box
          key={position}
          onPointerDown={(e) => startDrag(e, position)}
          sx={{
            position: "absolute",
            width: HANDLE_SIZE,
            height: HANDLE_SIZE,
            backgroundColor: "var(--global-neutral-white, #fff)",
            border: `2px solid ${region.color}`,
            cursor: isPanMode ? (isPanning ? "grabbing" : "grab") : cursor,
            touchAction: "none",
            ...style,
          }}
        />
      ))}
    </Box>
  );
};
